import React, { useEffect, useState } from 'react';
import { Button, Image, Modal, Typography, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import type { ProductionQuestion } from '../../../interfaces/ProductionQuestion.interface';
import { copyProductionQuestionReportToClipboard } from './copyProductionQuestionReport';
import { SCROLLABLE_MODAL_STYLES } from './constants';
import QuestionStatusControl from './QuestionStatusControl';
import { useGetLoggedInUserQuery } from '../../../redux/api/users';
import { canEditProductionQuestion } from '../../../utils/canEditProductionQuestion';
import { formatUserWithTimestamp } from './formatters';

const { Text } = Typography;

interface DetailFieldProps {
	label: string;
	children: React.ReactNode;
	fullWidth?: boolean;
}

const DetailField: React.FC<DetailFieldProps> = ({
	label,
	children,
	fullWidth = false,
}) => (
	<div className={fullWidth ? 'md:col-span-2' : undefined}>
		<Text strong>{label}</Text>
		<div className="mt-1">{children}</div>
	</div>
);

interface QuestionDetailsModalProps {
	question: ProductionQuestion | null;
	open: boolean;
	/** Sets open to false; content stays mounted until afterClose. */
	onClose: () => void;
	/** Clears the selected question once the close animation finishes. */
	afterClose?: () => void;
	onEdit?: () => void;
	showLineItemLinks?: boolean;
	lineItemId?: string;
	disabled?: boolean;
	onQuestionUpdated?: (updated: ProductionQuestion) => void;
}

const QuestionDetailsModal: React.FC<QuestionDetailsModalProps> = ({
	question,
	open,
	onClose,
	afterClose,
	onEdit,
	showLineItemLinks = false,
	lineItemId,
	disabled = false,
	onQuestionUpdated,
}) => {
	const [current, setCurrent] = useState<ProductionQuestion | null>(question);
	const [copying, setCopying] = useState(false);
	const { data: loggedInUser } = useGetLoggedInUserQuery();

	useEffect(() => {
		if (question) {
			setCurrent(question);
		}
	}, [question]);

	const handleAfterClose = () => {
		setCurrent(null);
		afterClose?.();
	};

	const record = current ?? question;
	const userCanEdit = record
		? canEditProductionQuestion(loggedInUser, record)
		: false;

	if (!record && !open) return null;

	const lineItems =
		record?.order_line_items?.length > 0
			? record.order_line_items
			: record?.order_line_item_id
			? [
					{
						id: record.order_line_item_id,
						order_name: record.order_name,
						line_item_name: record.line_item_name,
					},
				]
			: [];

	const handleUpdated = (updated: ProductionQuestion) => {
		setCurrent(updated);
		onQuestionUpdated?.(updated);
	};

	const handleCopyForEmail = async () => {
		if (!record) return;
		setCopying(true);
		try {
			await copyProductionQuestionReportToClipboard(record);
			message.success('Copied to clipboard — paste into your email');
		} catch {
			message.error('Could not copy to clipboard');
		} finally {
			setCopying(false);
		}
	};

	return (
		<Modal
			title={
				record ? (
					<span>
						Production Question{' '}
						<Text code copyable={{ text: record.uid }}>
							{record.uid}
						</Text>
					</span>
				) : (
					'Production Question'
				)
			}
			open={open}
			onCancel={onClose}
			afterClose={handleAfterClose}
			centered
			footer={[
				<Button
					key="copy"
					icon={<CopyOutlined />}
					loading={copying}
					onClick={handleCopyForEmail}
					disabled={!record}
				>
					Copy for Email
				</Button>,
				<Button key="close" onClick={onClose}>
					Close
				</Button>,
				...(onEdit
					? [
							<Button key="edit" type="primary" onClick={onEdit}>
								Edit
							</Button>,
						]
					: []),
			]}
			width={820}
			styles={SCROLLABLE_MODAL_STYLES}
		>
			{record && (
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<DetailField label="Category">
						{record.category_display}
					</DetailField>

					<DetailField label="Status">
						<QuestionStatusControl
							question={record}
							lineItemId={lineItemId}
							disabled={disabled || !userCanEdit}
							mode="field"
							onUpdated={handleUpdated}
						/>
					</DetailField>

					<DetailField label="Reported By">
						{formatUserWithTimestamp(
							record.reported_by,
							record.created_time
						)}
					</DetailField>

					<DetailField label="Resolved By">
						{formatUserWithTimestamp(
							record.resolved_by,
							record.resolved_at
						)}
					</DetailField>

					<DetailField
						label="Line Items"
						fullWidth={lineItems.length > 1}
					>
						{lineItems.length > 0 ? (
							<div className="flex flex-col gap-1">
								{lineItems.map((item) =>
									showLineItemLinks ? (
										<Link
											key={item.id}
											to={`/orders/line-items/${item.id}?tab=production-questions`}
										>
											{item.order_name} — {item.line_item_name}
										</Link>
									) : (
										<span key={item.id}>
											{item.order_name} — {item.line_item_name}
										</span>
									)
								)}
							</div>
						) : (
							'—'
						)}
					</DetailField>

					{record.description && (
						<DetailField label="Description" fullWidth>
							<div className="whitespace-pre-wrap">
								{record.description}
							</div>
						</DetailField>
					)}

					{record.resolution_source_display && (
						<DetailField label="Resolution Source">
							{record.resolution_source_display}
						</DetailField>
					)}

					{record.resolution_category_display && (
						<DetailField label="Resolution Category">
							{record.resolution_category_display}
						</DetailField>
					)}

					{record.resolution_description && (
						<DetailField label="Resolution" fullWidth>
							<div className="whitespace-pre-wrap">
								{record.resolution_description}
							</div>
						</DetailField>
					)}

					<DetailField label="Drawing Screenshots">
						{(record.drawing_attachments?.length ?? 0) > 0 ? (
							<div className="flex flex-col gap-2">
								<Image.PreviewGroup>
									{record.drawing_attachments.map((attachment) => (
										<Image
											key={attachment.id}
											src={attachment.url ?? undefined}
											rootClassName="w-full"
											className="rounded"
											styles={{
												image: { width: '100%', height: 'auto' },
											}}
										/>
									))}
								</Image.PreviewGroup>
							</div>
						) : (
							<Text type="secondary">None</Text>
						)}
					</DetailField>

					<DetailField label="3D Model Screenshots">
						{(record.model_attachments?.length ?? 0) > 0 ? (
							<div className="flex flex-col gap-2">
								<Image.PreviewGroup>
									{record.model_attachments.map((attachment) => (
										<Image
											key={attachment.id}
											src={attachment.url ?? undefined}
											rootClassName="w-full"
											className="rounded"
											styles={{
												image: { width: '100%', height: 'auto' },
											}}
										/>
									))}
								</Image.PreviewGroup>
							</div>
						) : (
							<Text type="secondary">None</Text>
						)}
					</DetailField>
				</div>
			)}
		</Modal>
	);
};

export default QuestionDetailsModal;
